import type { SessionStatus } from '@zeus/index'
import { useMemo, useState } from 'react'
import { useListSearch } from '~/hooks/use-list-search'
import { activeModuleFilter, filterSessions, sessionModuleOptions, sessionTagOptions, sessionTrainerOptions } from '~/pages/backoffice/admin/sessions/filter-sessions'

type FilterableSession = Parameters<typeof filterSessions>[0][number]

/** Un groupe proposé au filtre (#29), avec les ids de ses membres. */
export type FilterGroup = { id: string, name: string, memberIds: readonly string[] }

/**
 * L'état des filtres de la liste des parcours (#107, #29, #244) et la liste qui en
 * résulte. Un groupe ou un module qui disparaît des options cesse de filtrer :
 * un filtre invisible ne doit pas vider la liste en coulisse.
 *
 * `filtersKey` résume les filtres ACTIFS, pour la pagination (#198) : il change
 * à chaque critère modifié, et ramène la liste en page 1.
 */
export function useSessionFilters<T extends FilterableSession>(sessions: readonly T[], groups: readonly FilterGroup[]) {
  const { search, setSearch, query } = useListSearch()
  const [status, setStatus] = useState<SessionStatus | ''>('')
  const [moduleId, setModuleId] = useState('')
  const [groupId, setGroupId] = useState('')
  const [tagId, setTagId] = useState('')
  const [trainerId, setTrainerId] = useState('')

  const moduleOptions = useMemo(() => sessionModuleOptions(sessions), [sessions])
  const activeModuleId = activeModuleFilter(moduleId, moduleOptions)
  const activeGroup = groups.find(group => group.id === groupId)
  const tagOptions = useMemo(() => sessionTagOptions(sessions), [sessions])
  const activeTagId = tagOptions.some(tag => tag.id === tagId) ? tagId : ''
  const trainerOptions = useMemo(() => sessionTrainerOptions(sessions), [sessions])
  const activeTrainerId = trainerOptions.some(trainer => trainer.id === trainerId) ? trainerId : ''
  const visible = useMemo(() => filterSessions(sessions, {
    query,
    status,
    moduleId: activeModuleId,
    groupMemberIds: activeGroup ? new Set(activeGroup.memberIds) : null,
    tagId: activeTagId,
    trainerId: activeTrainerId,
  }), [sessions, query, status, activeModuleId, activeGroup, activeTagId, activeTrainerId])

  const filtersKey = [query, status, activeModuleId, activeGroup?.id ?? '', activeTagId, activeTrainerId].join('|')

  const reset = () => {
    setSearch('')
    setStatus('')
    setModuleId('')
    setGroupId('')
    setTagId('')
    setTrainerId('')
  }

  return {
    search,
    setSearch,
    query,
    status,
    setStatus,
    moduleOptions,
    moduleId: activeModuleId,
    setModuleId,
    groupId: activeGroup?.id ?? '',
    setGroupId,
    tagOptions,
    tagId: activeTagId,
    setTagId,
    trainerOptions,
    trainerId: activeTrainerId,
    setTrainerId,
    visible,
    filtersKey,
    reset,
  }
}

export type SessionFiltersState = ReturnType<typeof useSessionFilters>
